import React, { useState, useEffect, useRef, useCallback } from "react";
import { IoIosSend, IoIosClose, IoIosRemove } from "react-icons/io";
import { loadGenesysSDK, setUserAttributes, sendMessage, closeSession } from "../Services/GenesysChatService";

const EMPTY_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  cellphone: "",
  practiceDetails: "",
  medicalOption: "",
  mainMemberIdNumber: "",
  medicalAidNumber: "",
  dependentSurname: "",
  mainMemberDob: "",
  dependentDob: "",
};

const S = {
  panel: {
    position: "fixed",
    bottom: "24px",
    right: "24px",
    width: "340px",
    background: "#fff",
    borderRadius: "12px",
    boxShadow: "0 8px 32px rgba(0,0,0,0.22)",
    display: "flex",
    flexDirection: "column",
    zIndex: 9999,
    overflow: "hidden",
    fontFamily: "sans-serif",
  },
  header: {
    background: "#3d3d3d",
    color: "#fff",
    padding: "12px 16px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontWeight: 600,
    fontSize: "15px",
  },
  input: {
    padding: "8px 10px",
    borderRadius: "6px",
    border: "1px solid #ddd",
    fontSize: "13px",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  },
  btn: {
    background: "#3498db",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    padding: "10px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: 500,
  },
};

const CustomChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [phase, setPhase] = useState("form");
  const [form, setForm] = useState(EMPTY_FORM);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  const messagesEndRef = useRef(null);
  const phaseRef = useRef("form");
  const seenIdsRef = useRef(new Set());
  const hasStartedConversationRef = useRef(false);
  const subscriptionsRegisteredRef = useRef(false);

  const applyChatPrefill = useCallback((prefill = {}) => {
    const resolvedMainMemberDob =
      prefill.mainMemberDob ??
      prefill.MainMemberDob ??
      prefill.dateofbirth ??
      prefill.DateOfBirth ??
      prefill.mainMemberDateOfBirth;
    const resolvedDependentDob =
      prefill.dependentDob ??
      prefill.DependentDob ??
      prefill.dependantDob ??
      prefill.DependantDob ??
      prefill.dependantDateOfBirth ??
      prefill.DependantDateOfBirth;

    setForm((prev) => ({
      ...prev,
      email: prefill.email ?? prev.email,
      cellphone: prefill.cellphone ?? prefill.cellnumber ?? prev.cellphone,
      mainMemberIdNumber:
        prefill.mainMemberIdNumber ??
        prefill.mainmemberidnumber ??
        prefill.MainMemberIDNumber ??
        prev.mainMemberIdNumber,
      medicalAidNumber: prefill.medicalAidNumber ?? prefill.medicalaidnumber ?? prev.medicalAidNumber,
      mainMemberDob: resolvedMainMemberDob ?? prev.mainMemberDob,
      dependentDob: resolvedDependentDob ?? prev.dependentDob,
    }));
  }, []);

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  useEffect(() => {
    const open = (prefill) => {
      if (prefill) {
        applyChatPrefill(prefill);
      } else if (window.PPNChatPrefill) {
        applyChatPrefill(window.PPNChatPrefill);
      }
      setIsOpen(true);
      setIsMinimized(false);
    };

    window.PPNCustomChat = { open };
    window.openCustomChat = open;

    const onOpenCustomChat = () => open(window.PPNChatPrefill);
    window.addEventListener("ppn:open-custom-chat", onOpenCustomChat);

    return () => {
      delete window.PPNCustomChat;
      delete window.openCustomChat;
      window.removeEventListener("ppn:open-custom-chat", onOpenCustomChat);
    };
  }, [applyChatPrefill]);

  useEffect(() => {
    const onPrefillUpdate = (event) => {
      applyChatPrefill(event?.detail || {});
    };

    if (window.PPNChatPrefill) {
      applyChatPrefill(window.PPNChatPrefill);
    }

    window.addEventListener("ppn:chat-prefill-updated", onPrefillUpdate);
    return () => window.removeEventListener("ppn:chat-prefill-updated", onPrefillUpdate);
  }, [applyChatPrefill]);

  const ensureGenesysSubscriptions = useCallback(async () => {
    await loadGenesysSDK();
    if (subscriptionsRegisteredRef.current) return;

    window.Genesys("subscribe", "MessagingService.messagesReceived", (data) => {
      if (!hasStartedConversationRef.current) return;

      const payload = data?.data || data;
      let incoming = [];

      if (Array.isArray(payload?.messages)) incoming = payload.messages;
      else if (payload?.message) incoming = [payload.message];
      else if (Array.isArray(payload)) incoming = payload;
      else if (payload?.text) incoming = [payload];

      incoming.forEach((msg) => {
        if (!msg.text && !msg.content) return;
        const text = msg.text || msg.content?.[0]?.text || "Unsupported message format";
        const msgId = msg.id || msg.channel?.messageId || `${Date.now()}-${Math.random()}`;
        if (seenIdsRef.current.has(msgId)) return;
        seenIdsRef.current.add(msgId);

        setMessages((prev) => {
          if (prev.some((item) => item.text === text && !item.isAgent)) return prev;
          return [
            ...prev,
            {
              id: msgId,
              text,
              sender: msg.direction === "Outbound" ? "Agent" : `Agent/Bot (${msg.direction || "Unknown"})`,
              isAgent: true,
            },
          ];
        });
      });
    });

    window.Genesys("subscribe", "MessagingService.conversationDisconnected", () => {
      setMessages((prev) => [...prev, { id: Date.now(), text: "The session has ended.", isSystem: true }]);
      setPhase("ended");
    });

    subscriptionsRegisteredRef.current = true;
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleStart = async (e) => {
    e.preventDefault();
    setError("");
    hasStartedConversationRef.current = true;
    phaseRef.current = "connecting";
    setPhase("connecting");

    try {
      await ensureGenesysSubscriptions();
      setUserAttributes(form);
      seenIdsRef.current.clear();

      const initialMessage = `Name: ${form.firstName} ${form.lastName}\nEmail: ${
        form.email || "N/A"
      }\nCell Number: ${form.cellphone || "N/A"}\nPractice Details: ${
        form.practiceDetails || "N/A"
      }\nMedical Option: ${form.medicalOption || "N/A"}\nMain Member ID Number: ${
        form.mainMemberIdNumber || "N/A"
      }\nMedical Aid Number: ${form.medicalAidNumber || "N/A"}\nMain Member DOB: ${
        form.mainMemberDob || "N/A"
      }\nDependent Surname: ${form.dependentSurname || "N/A"}\nDependent DOB: ${form.dependentDob || "N/A"}`;

      setMessages([
        { id: "sys-start", text: "Connected. Type your message below.", isSystem: true },
        { id: Date.now(), text: initialMessage, sender: form.firstName || "You", isAgent: false },
      ]);
      phaseRef.current = "chat";
      setPhase("chat");
      sendMessage(initialMessage);
    } catch (err) {
      console.error(err);
      setError("Unable to connect. Please try again.");
      hasStartedConversationRef.current = false;
      phaseRef.current = "form";
      setPhase("form");
    }
  };

  const handleSend = () => {
    const text = input.trim();
    if (!text) return;
    setInput("");
    setMessages((prev) => [...prev, { id: Date.now(), text, sender: form.firstName || "You", isAgent: false }]);
    sendMessage(text);
  };

  const handleEnd = () => {
    closeSession();
    hasStartedConversationRef.current = false;
    phaseRef.current = "ended";
    setPhase("ended");
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
      if (phaseRef.current === "chat") handleEnd();
      setPhase("form");
      setMessages([]);
      setForm(EMPTY_FORM);
      setError("");
      seenIdsRef.current.clear();
      hasStartedConversationRef.current = false;
      phaseRef.current = "form";
    }, 400);
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        ...S.panel,
        transform: isClosing ? "translateY(120%)" : "translateY(0)",
        opacity: isClosing ? 0 : 1,
        transition: "transform 0.4s ease-in, opacity 0.4s ease-in",
      }}
    >
      <div style={S.header}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span>Live Chat</span>
        </div>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <IoIosRemove size={22} style={{ cursor: "pointer" }} title="Minimize" onClick={() => setIsMinimized((m) => !m)} />
          <IoIosClose size={24} style={{ cursor: "pointer" }} title="Close" onClick={handleClose} />
        </div>
      </div>

      {!isMinimized && (
        <>
          {phase === "form" && (
            <form onSubmit={handleStart} style={{ padding: "16px", display: "flex", flexDirection: "column", gap: "10px" }}>
              <p style={{ margin: 0, fontSize: "13px", color: "#555" }}>Enter your details to start chatting.</p>
              <input
                required
                placeholder="First Name"
                value={form.firstName}
                onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
                style={S.input}
              />
              <input
                required
                placeholder="Last Name"
                value={form.lastName}
                onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
                style={S.input}
              />
              <input
                type="email"
                placeholder="Email (optional)"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                style={S.input}
              />
              <input
                placeholder="Cell Number"
                value={form.cellphone}
                onChange={(e) => setForm((f) => ({ ...f, cellphone: e.target.value }))}
                style={S.input}
              />
              <input
                placeholder="Practice Details"
                value={form.practiceDetails}
                onChange={(e) => setForm((f) => ({ ...f, practiceDetails: e.target.value }))}
                style={S.input}
              />
              <input
                placeholder="Medical Option"
                value={form.medicalOption}
                onChange={(e) => setForm((f) => ({ ...f, medicalOption: e.target.value }))}
                style={S.input}
              />
              <input
                placeholder="Main Member ID Number"
                value={form.mainMemberIdNumber}
                onChange={(e) => setForm((f) => ({ ...f, mainMemberIdNumber: e.target.value }))}
                style={S.input}
              />
              <input
                placeholder="Medical Aid Number"
                value={form.medicalAidNumber}
                onChange={(e) => setForm((f) => ({ ...f, medicalAidNumber: e.target.value }))}
                style={S.input}
              />
              <input
                placeholder="Main Member DOB"
                value={form.mainMemberDob}
                onChange={(e) => setForm((f) => ({ ...f, mainMemberDob: e.target.value }))}
                style={S.input}
              />
              <input
                placeholder="Dependent Surname"
                value={form.dependentSurname}
                onChange={(e) => setForm((f) => ({ ...f, dependentSurname: e.target.value }))}
                style={S.input}
              />
              <input
                placeholder="Dependent DOB"
                value={form.dependentDob}
                onChange={(e) => setForm((f) => ({ ...f, dependentDob: e.target.value }))}
                style={S.input}
              />
              {error && <p style={{ color: "#c00", fontSize: "12px", margin: 0 }}>{error}</p>}
              <button type="submit" style={S.btn}>
                Start Chat
              </button>
            </form>
          )}

          {phase === "connecting" && (
            <div style={{ padding: "24px", textAlign: "center", color: "#777", fontSize: "13px" }}>Connecting to an agent...</div>
          )}

          {phase === "chat" && (
            <>
              <div
                style={{
                  overflowY: "auto",
                  padding: "12px",
                  maxHeight: "300px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                {messages.map((m) => (
                  <div key={m.id} style={{ alignSelf: m.isSystem ? "center" : m.isAgent ? "flex-start" : "flex-end" }}>
                    {m.isSystem ? (
                      <span style={{ fontSize: "11px", color: "#999", fontStyle: "italic" }}>{m.text}</span>
                    ) : (
                      <div
                        style={{
                          background: m.isAgent ? "#e8f0fe" : "#1a3a5c",
                          color: m.isAgent ? "#222" : "#fff",
                          borderRadius: "10px",
                          padding: "8px 12px",
                          fontSize: "13px",
                          maxWidth: "240px",
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        <div style={{ fontSize: "10px", fontWeight: 600, marginBottom: "3px", opacity: 0.7 }}>{m.sender}</div>
                        {m.text}
                      </div>
                    )}
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
              {error && <p style={{ color: "#c00", fontSize: "12px", margin: "0 12px" }}>{error}</p>}
              <div style={{ display: "flex", borderTop: "1px solid #eee", padding: "8px", gap: "6px" }}>
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSend()}
                  placeholder="Type a message..."
                  style={{ ...S.input, flex: 1 }}
                />
                <button onClick={handleSend} style={{ ...S.btn, padding: "8px 12px", display: "flex", alignItems: "center" }}>
                  <IoIosSend size={18} />
                </button>
              </div>
              <div style={{ padding: "6px 12px 10px", textAlign: "right" }}>
                <span onClick={handleEnd} style={{ fontSize: "11px", color: "#aaa", cursor: "pointer", textDecoration: "underline" }}>
                  End chat
                </span>
              </div>
            </>
          )}

          {phase === "ended" && (
            <div style={{ padding: "24px", textAlign: "center", color: "#555", display: "flex", flexDirection: "column", gap: "12px" }}>
              <p style={{ margin: 0 }}>Chat ended. Thank you!</p>
              <button
                style={S.btn}
                onClick={() => {
                  setPhase("form");
                  setMessages([]);
                  seenIdsRef.current.clear();
                }}
              >
                Start New Chat
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CustomChatWidget;
